import { computed, ComputedRef } from '@vue/composition-api';
import { formatSize } from '@/renderer/scripts/utils/tools';
import store from '@/renderer/store';
import ipc, { IpcEvents } from '@/renderer/scripts/utils/ipc';

type SpaceCenter = {
  usedSpace: ComputedRef<string>;
  leftSpace: ComputedRef<string>;
  totalSpace: ComputedRef<string>;
  percentSpace: ComputedRef<number>;
  hasPersonalSpace: ComputedRef<boolean>;
  isShareSpace: ComputedRef<boolean>;
  versionType: ComputedRef<VersionType>;
};
export enum VersionType {
  free = 'official-free', // 免费版本
  test = 'official-test', // 体验版本
  personal = 'official-personal', // 个人版本
  team = 'official-team', // 团队版本
  enterpris = 'official-enterpris', // 企业版本
}

export default function useSpaceCenter(): SpaceCenter {
  // 已使用容量
  const hasPersonalSpace = computed(() => store.getters.hasPersonalSpace);
  const userCapacity = computed(() => store.getters.capacity);
  const isShareSpace = computed(() => !userCapacity.value);
  const orgCapacity = computed(() => store.getters.orgSpace);
  const orgAvaSpace = computed(() => store.getters.remainingSpaceQuota);
  const orgFileSize = computed(() => store.getters.totalFileSize);
  const userFileSize = computed(() => store.getters.userFileSize);
  const usedSpace = computed(() => {
    let usedInfo = { size: 0, unit: 'B' };
    if (store.getters.editionFlag === 'team') {
      usedInfo = formatSize(`${orgFileSize.value}`, false, true);
    } else {
      usedInfo = formatSize(`${userFileSize.value}`, false, true);
    }
    const usedSpaceStr = `${usedInfo.size || 0}${usedInfo.unit || 'B'}`;
    ipc.send(IpcEvents.UPDATE_MENU_DATA, { event: 'usedSpace', param: usedSpaceStr });
    if (!hasPersonalSpace.value) {
      ipc.send(IpcEvents.UPDATE_MENU_DATA, { event: 'totalSpace', param: '0B' });
    }
    return usedSpaceStr;
  });
  const leftSpace = computed(() => {
    let leftInfo = { size: 0, unit: 'B' };
    let userSize = 0;
    if (store.getters.editionFlag === 'team') {
      userSize = orgFileSize.value;
    } else {
      userSize = userFileSize.value;
    }
    let totalSize = 0;
    if (userCapacity.value === '') {
      // 个人空间使用企业剩余额度
      if (['personal', 'team'].includes(store.getters.editionFlag)) {
        totalSize = orgCapacity.value;
      } else {
        totalSize = orgAvaSpace.value;
      }
    } else {
      totalSize = userCapacity.value;
    }
    leftInfo = formatSize(totalSize - userSize, false, true);
    return `${leftInfo.size || 0}${leftInfo.unit || 'B'}`;
  });
  const totalSpace = computed(() => {
    let totalInfo = { size: 0, unit: 'B' };
    if (userCapacity.value === '') {
      // 个人空间使用企业剩余额度
      if (['personal', 'team'].includes(store.getters.editionFlag)) {
        totalInfo = formatSize(orgCapacity.value as string, false, true);
      } else {
        totalInfo = formatSize(orgAvaSpace.value as string, false, true);
      }
    } else {
      totalInfo = formatSize(userCapacity.value as string, false, true);
    }
    const totalSpaceStr = `${totalInfo.size || 0}${totalInfo.unit || 'B'}`;
    if (hasPersonalSpace.value) {
      ipc.send(IpcEvents.UPDATE_MENU_DATA, { event: 'totalSpace', param: totalSpaceStr });
    } else {
      ipc.send(IpcEvents.UPDATE_MENU_DATA, { event: 'totalSpace', param: '0B' });
    }
    return totalSpaceStr;
  });
  const percentSpace = computed(() => {
    // ‘+’字符串转数字
    const used: number = +userFileSize.value || 0;
    const userTotal = userCapacity.value ? +userCapacity.value : +orgCapacity.value;
    return (used / userTotal) * 100;
  });
  const versionType = computed(() => {
    let type = VersionType.enterpris;
    if (store.getters.orgChannel && !store.getters.channelUpdated) {
      // 体验版
      type = VersionType.test;
    } else if (store.getters.editionFlag === 'personal') {
      // 个人版
      type = VersionType.personal;
    } else if (store.getters.editionFlag === 'team') {
      // 团队版
      type = VersionType.team;
    }
    return type;
  });
  return { usedSpace, leftSpace, totalSpace, percentSpace, hasPersonalSpace, isShareSpace, versionType };
}
